import { ARTI } from '@tacans/shared-marketplace-layer';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteE } from 'src/constants/pages.constants';
import { ItemPlaceholder } from 'src/features/common/shared/components/item-placeholder';
import { useAppDispatch } from 'src/hooks/app';
import { Grid } from 'src/ui/common/atoms/grid';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { Typography } from 'src/ui/common/atoms/typography';
import { Badge } from 'src/ui/white-labels/techcontemporary/atoms/badge';
import { BuyNowCard } from 'src/ui/white-labels/techcontemporary/atoms/card';
import { fetchArtsThunk } from '../../../explore/ducks/explore-arts/explore-arts.thunk';
import { useArtsByPage, useArtsEntities, usePages, useTotal } from '../../../explore/hooks/use-explore-arts';
import { useExploreComputedItems } from '../../../explore/hooks/use-explore-computed-items';
import { StyledButton, StyledButtonWrapper, StyledHeading, StyledWrapper } from './styles';

export const LandingCardsSection = () => {
    const { t } = useTranslation('tech-contemporary');
    const router = useRouter();
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(fetchArtsThunk());
    }, []);

    const items = useExploreComputedItems<ARTI>({
        limit: 3,
        useEntities: useArtsEntities,
        useByPage: useArtsByPage,
        useTotal,
        usePages,
    });

    const recentArts = items.slice(0, 6);

    const locale = useMemo(
        () => ({
            heading: t('landing.cards-section-heading', {
                ns: 'tech-contemporary',
            }),
            viewMore: t('landing.actions.view-more-art', {
                ns: 'tech-contemporary',
            }),
        }),
        [t]
    );

    const goToExplore = useCallback(() => {
        router.push(RouteE.EXPLORE);
    }, [router]);

    return (
        <StyledWrapper>
            <StyledHeading medium size="md" color="white" variant="title">
                {locale.heading}
            </StyledHeading>
            {!recentArts.length ? (
                <Badge type="success" text="No recently added arts" />
            ) : (
                <>
                    <Spacing paddingSm="40px 0">
                        <Grid xs={1} sm={3} md={3} rowGap={24} columnGapMd={28} columnGapSm={12}>
                            {recentArts.map((art, index) => (
                                <div key={art?.id ?? index}>
                                    {art ? (
                                        <BuyNowCard
                                            isLanding
                                            testId="infinite-explore-item"
                                            art={{ ...art, copiesLeft: art.copiesLeft || 0 }}
                                        />
                                    ) : (
                                        <ItemPlaceholder />
                                    )}
                                </div>
                            ))}
                        </Grid>
                    </Spacing>
                    <StyledButtonWrapper>
                        <StyledButton onClick={goToExplore} variant="tonal" size="lg">
                            <Typography>{locale.viewMore}</Typography>
                            <SvgIcon variant="regular" icon="arrowRight" />
                        </StyledButton>
                    </StyledButtonWrapper>
                </>
            )}
        </StyledWrapper>
    );
};